import { useCallback, useEffect, useState } from "react";
import { Loader2, ExternalLink, Mail, Upload, GitCompare, FileWarning, Repeat, Trash2,
  FileDown, Lock, Unlock, CalendarCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";
import FrameworkEmailDialog from "../../components/FrameworkEmailDialog";
import SwapDialog from "./SwapDialog";
import { openInSubBuilder } from "./navigation";

/** One package: what is in it, what is missing, and what the returned review means for the FRS. */
export default function PackageDetail({ project, pkgId, canEdit, onChanged, onUploadReturned }) {
  const [pkg, setPkg] = useState(null);
  const [proposals, setProposals] = useState(null);
  const [draft, setDraft] = useState(null);
  const [swapItem, setSwapItem] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sentOn, setSentOn] = useState("");

  const load = useCallback(async () => {
    try {
      const [d, pr] = await Promise.all([
        opsApi.submittal(project.id, pkgId),
        opsApi.submittalFrsProposals(project.id, pkgId),
      ]);
      setPkg(d);
      setProposals(pr);
    } catch { toast.error("Could not open that package"); }
  }, [project.id, pkgId]);
  useEffect(() => { load(); }, [load]);

  const move = async (status) => {
    setBusy(true);
    try {
      const res = await opsApi.submittalStatus(project.id, pkgId, { status });
      if (res.revision) toast.info(`${res.revision.number} Rev ${res.revision.revision} created`);
      toast.success(`Now ${status.replace(/_/g, " ")}`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That status change was refused"); }
    finally { setBusy(false); }
  };

  const email = async () => {
    setBusy(true);
    try {
      const d = await opsApi.emailDraft(project.id, "submittal", pkgId);
      setDraft(d);
    } catch { toast.error("Could not prepare that email"); }
    finally { setBusy(false); }
  };

  const applyFrs = async () => {
    setBusy(true);
    try {
      const res = await opsApi.submittalFrsApply(project.id, pkgId, { accept: [] });
      toast.success(`${res.applied} FRS row(s) updated with the submitted product`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "The FRS could not be updated"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete draft ${pkg.number}? It has never gone out.`)) return;
    setBusy(true);
    try {
      await opsApi.submittalDelete(project.id, pkgId);
      toast.success(`${pkg.number} deleted`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That package could not be deleted"); }
    finally { setBusy(false); }
  };

  const unlock = async () => {
    const reason = window.prompt(
      `${pkg.number} is ${pkg.status_label} and read-only. Why does it need to change?`);
    if (!reason?.trim()) return;
    setBusy(true);
    try {
      await opsApi.submittalUnlock(project.id, pkgId, reason.trim());
      toast.success(`${pkg.number} unlocked — the reason is on the job log`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That override was refused"); }
    finally { setBusy(false); }
  };

  const markSubmitted = async () => {
    setBusy(true);
    try {
      await opsApi.submittalMarkSubmitted(project.id, pkgId, { submitted_on: sentOn });
      toast.success(`${pkg.number} recorded as submitted`);
      setSentOn("");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be recorded"); }
    finally { setBusy(false); }
  };

  const download = () => opsApi.submittalPdfDownload(project.id, pkgId, `${pkg.number}.pdf`)
    .catch((e) => toast.error(e?.response?.data?.detail
      || "There is no compiled PDF yet — finalize the package in SubBuilder"));

  const edit = () => openInSubBuilder({
    projectId: pkg.subbuilder_project_id, submittalId: pkg.subbuilder_submittal_id,
    number: pkg.number, opsProjectId: project.id,
    opsProjectName: project.identity?.name || project.display || "",
    jobNumber: project.identity?.job_number || project.job_number,
  });

  if (!pkg) {
    return <div className="flex items-center gap-2 py-6 text-xs text-slate-500">
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Opening…
    </div>;
  }

  return (
    <div className="space-y-4" data-testid={`ops-submittal-detail-${pkg.number}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold text-slate-900">
          {pkg.number}{pkg.revision ? ` Rev ${pkg.revision}` : ""} — {pkg.title}
        </h4>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600"
              title={pkg.status_meaning}>{pkg.status_label}</span>
        {pkg.spec_section && (
          <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-500">
            spec {pkg.spec_section}
          </span>
        )}
        {pkg.procurement_safe && (
          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700">
            safe to buy
          </span>
        )}
        {pkg.missing_cut_sheets > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800"
                data-testid="ops-submittal-missing-cutsheets">
            <FileWarning className="h-3 w-3" /> {pkg.missing_cut_sheets} cut sheet(s) missing
          </span>
        )}
        <span className="ml-auto text-xs text-slate-500">
          {pkg.items.length} item(s) from the Current FRS ·
          builder {pkg.builder.compiled ? "compiled" : pkg.builder.status || "draft"}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={edit}
                data-testid="ops-submittal-open-builder">
          <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Edit in SubBuilder
        </Button>
        <Button size="sm" variant="outline" onClick={download}
                data-testid="ops-submittal-download">
          <FileDown className="mr-1.5 h-3.5 w-3.5" /> Download PDF
        </Button>
        {canEdit && (
          <Button size="sm" variant="outline" onClick={email} disabled={busy || !pkg.pdf_ready}
                  title={pkg.pdf_ready ? "" : "Finalize the package in SubBuilder first"}
                  data-testid="ops-submittal-email">
            <Mail className="mr-1.5 h-3.5 w-3.5" /> Email submittal
          </Button>
        )}
        {canEdit && pkg.sendable && (
          <span className="inline-flex items-center gap-1" data-testid="ops-submittal-mark-sent">
            <Input type="date" value={sentOn} onChange={(e) => setSentOn(e.target.value)}
                   className="h-8 w-36 text-xs" data-testid="ops-submittal-sent-on" />
            <Button size="sm" variant="ghost" onClick={markSubmitted} disabled={busy}
                    data-testid="ops-submittal-mark-submitted"
                    className="text-slate-600">
              <CalendarCheck className="mr-1.5 h-3.5 w-3.5" /> Mark as submitted
            </Button>
          </span>
        )}
        {canEdit && (
          <Button size="sm" variant="outline" onClick={() => onUploadReturned(pkg.id)}
                  data-testid="ops-submittal-upload-returned">
            <Upload className="mr-1.5 h-3.5 w-3.5" /> File the returned review
          </Button>
        )}
        {canEdit && pkg.status === "not_submitted" && !pkg.submitted_at
          && !(pkg.swaps || []).length && !pkg.revision && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={remove}
                  data-testid="ops-submittal-delete"
                  className="text-slate-500 hover:text-rose-700">
            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete draft
          </Button>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          {canEdit && (pkg.transitions || []).map((t) => (
            <Button key={t.status} size="sm" variant="outline" title={t.meaning} disabled={busy}
                    onClick={() => move(t.status)} data-testid={`ops-submittal-to-${t.status}`}>
              {t.label}
            </Button>
          ))}
        </div>
      </div>

      {pkg.locked && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
             data-testid="ops-submittal-readonly">
          <Lock className="h-3.5 w-3.5" />
          <span><span className="font-semibold">{pkg.status_label}</span> — this package is
            read-only in SubBuilder too. What the GC approved is what got approved.</span>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={unlock} disabled={busy}
                    className="ml-auto h-7 text-xs" data-testid="ops-submittal-unlock">
              <Unlock className="mr-1.5 h-3 w-3" /> Override with a reason
            </Button>
          )}
        </div>
      )}

      {pkg.lock_override?.reason && (
        <p className="text-xs text-amber-800" data-testid="ops-submittal-override-note">
          Override: {pkg.lock_override.reason} — {pkg.lock_override.by}{" "}
          {(pkg.lock_override.at || "").slice(0, 10)}
        </p>
      )}

      {pkg.frs_changed && (
        <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-800"
             data-testid="ops-submittal-frs-changed">
          {pkg.frs_change_note}
        </div>
      )}

      {pkg.comments && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
             data-testid="ops-submittal-comments">
          <span className="font-medium">Reviewer comments:</span> {pkg.comments}
        </div>
      )}

      {(pkg.swaps || []).length > 0 && (
        <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-800"
             data-testid="ops-submittal-swaps">
          <p className="font-semibold">Product swaps recorded (bid vs submitted)</p>
          <ul className="mt-1 space-y-0.5">
            {Object.values((pkg.swaps || []).reduce((acc, s) => {
              const key = s.group_id || s.id;
              const g = acc[key] || { ...s, rooms: 0, applied: 0 };
              g.rooms += 1;
              g.applied += s.applied_to_frs ? 1 : 0;
              acc[key] = g;
              return acc;
            }, {})).map((s) => (
              <li key={s.group_id || s.id}>
                {s.bid_product?.product_code || "bid product"} →{" "}
                {s.submitted_product?.product_code || "submitted product"}
                {` · ${s.rooms} room(s)`}
                {s.reason ? ` · ${s.reason}` : ""}
                {s.cost_impact ? " · not like-for-like, check the money" : ""}
                {s.applied === s.rooms ? " · in the FRS" : " · FRS requires review"}
              </li>
            ))}
          </ul>
          {proposals?.proposals?.length > 0 && canEdit && (
            <Button size="sm" className="mt-2" onClick={applyFrs} disabled={busy}
                    data-testid="ops-submittal-apply-frs">
              <GitCompare className="mr-1.5 h-3.5 w-3.5" />
              Write {proposals.proposals.length} change(s) to the Current FRS
            </Button>
          )}
        </div>
      )}

      <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-1.5 text-left">Room</th>
              <th className="px-3 py-1.5 text-left">Product</th>
              <th className="px-3 py-1.5 text-left">Manufacturer</th>
              <th className="px-3 py-1.5 text-left">Model</th>
              <th className="px-3 py-1.5 text-right">Qty</th>
              <th className="px-3 py-1.5 text-left">Cut sheet</th>
              {canEdit && <th className="px-3 py-1.5 text-right">Swap</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pkg.items.length === 0 && (
              <tr><td colSpan={canEdit ? 7 : 6} className="px-3 py-4 text-center text-slate-500">
                No FRS rows on this package.
              </td></tr>
            )}
            {pkg.items.slice(0, 300).map((i, idx) => (
              <tr key={idx} className={i.cut_sheet?.found ? "" : "bg-amber-50/50"}
                  data-testid={`ops-submittal-item-${idx}`}>
                <td className="px-3 py-1.5">{i.room || "—"}</td>
                <td className="px-3 py-1.5">{i.current_product_name || i.product_name || "—"}</td>
                <td className="px-3 py-1.5">{i.current_manufacturer || "—"}</td>
                <td className="px-3 py-1.5 font-mono">
                  {i.current_model || "—"}
                  {i.swapped && (
                    <span className="ml-1 text-[10px] font-sans text-violet-700"
                          title={`Bid: ${[i.manufacturer, i.model].filter(Boolean).join(" ")}`}>
                      swapped
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{i.qty}</td>
                <td className="px-3 py-1.5">
                  {i.cut_sheet?.found
                    ? <span className="text-emerald-700">{i.cut_sheet.filename || "in the library"}</span>
                    : <span className="text-amber-700">not found</span>}
                </td>
                {canEdit && (
                  <td className="px-3 py-1 text-right">
                    <button onClick={() => setSwapItem(i)} title="Record what was submitted instead"
                            data-testid={`ops-submittal-swap-${idx}`}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700">
                      <Repeat className="h-3 w-3" /> Swap
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(pkg.revisions || []).length > 1 && (
        <p className="text-xs text-slate-500" data-testid="ops-submittal-revisions">
          Revisions: {pkg.revisions.map((r) => `Rev ${r.revision} (${r.status.replace(/_/g, " ")})`)
            .join(" · ")}
        </p>
      )}

      {swapItem && (
        <SwapDialog projectId={project.id} pkgId={pkg.id} item={swapItem}
                    matching={pkg.items.filter(
                      (i) => (i.current_manufacturer || i.manufacturer || "")
                        === (swapItem.current_manufacturer || swapItem.manufacturer || "")
                        && (i.current_model || i.model || "")
                        === (swapItem.current_model || swapItem.model || "")).length}
                    onClose={() => setSwapItem(null)}
                    onDone={() => { load(); onChanged?.(); }} />
      )}

      {draft && (
        <FrameworkEmailDialog projectId={project.id} draft={draft} onClose={() => setDraft(null)}
                              onSent={() => { load(); onChanged?.(); }} />
      )}
    </div>
  );
}
